import { FC, createContext, useContext, ReactNode, useState } from "react";
import _noop from "lodash/noop";

import { useDisclosure } from "shared/lib/hooks/use-disclosure";
import { EMAIL_TEMPLATES_TABS } from "./constants";
import { ValueOfObjectFields } from "shared/lib/interfaces/utils";
import {
  EmailAIGenerationI,
  EmailTemplateDraftsI,
  EmailTemplatesManagerI,
  useAccountEmailAIGeneration,
  useEmailTemplateDrafts,
  useEmailTemplatesManager,
} from "@/hooks/use-email-templates-manager";
import {
  useEmailTemplateSequencingContext,
  EmailTemplateSequencingContextI,
} from "@/hooks/use-email-templates-manager/use-email-template-followup";
import { useSelectorEmailAIGenerationOptionalParams } from "./selectors";
import { EmailDraftI } from "./interfaces";

// Sidebar Context
type EmailTemplatesSidebarContextI = Omit<
  ReturnType<typeof useDisclosure>,
  "onToggle"
>;

export const EmailTemplatesSidebarContext =
  createContext<EmailTemplatesSidebarContextI>({
    isOpen: false,
    onOpen: _noop,
    onClose: _noop,
  });

export const useEmailTemplatesSidebarContext = () =>
  useContext(EmailTemplatesSidebarContext);

// Email Templates Context
type EmailTemplatesContextI = EmailTemplatesManagerI &
  EmailTemplateDraftsI &
  EmailAIGenerationI &
  EmailTemplateSequencingContextI & {
    activeTab: ValueOfObjectFields<typeof EMAIL_TEMPLATES_TABS>;
    setActiveTab: (
      tab: ValueOfObjectFields<typeof EMAIL_TEMPLATES_TABS>
    ) => void;

    initialDraft?: EmailDraftI;
    setInitialDraft: (draft?: EmailDraftI) => void;
  };

export const EmailTemplatesContext = createContext<EmailTemplatesContextI>({
  activeTab: EMAIL_TEMPLATES_TABS.COMPOSE,
  setActiveTab: _noop,

  initialDraft: undefined,
  setInitialDraft: _noop,

  //EmailTemplatesManagerI
  templates: undefined,
  refetchTemplates: () => Promise.resolve({} as any),
  activeTemplate: undefined,
  activeTemplateId: undefined,
  setActiveTemplateId: _noop,
  isEditMode: undefined,
  setEditMode: _noop,
  emailGenerationConfig: undefined,
  setEmailGenerationConfig: _noop,
  campaignAttachmentsData: null,
  isAIGenerationComplete: false,
  isAIGenerationStarted: false,
  isEmailSent: false,
  emailData: undefined,
  handleComposeWithAI: () => Promise.resolve(undefined),
  setEmailData: _noop,
  setIsEmailSent: _noop,
  setIsAIGenerationComplete: _noop,
  setIsAIGenerationStarted: _noop,

  //EmailTemplateDraftsI
  drafts: {},
  setDrafts: _noop,
  setDraft: _noop,
  getDraft: () => undefined,

  //EmailTemplateSequencingContextI
  activeEmailSequenceItemId: undefined,
  setActiveEmailSequenceItemId: _noop,
});

export const useEmailTemplatesContext = () => useContext(EmailTemplatesContext);

interface EmailTemplatesProviderPropsI {
  children: ReactNode;
}

// Sidebar Provider
export const EmailTemplatesSidebarProvider: FC<
  EmailTemplatesProviderPropsI
> = ({ children }) => {
  const disclosure = useDisclosure();

  return (
    <EmailTemplatesSidebarContext.Provider value={{ ...disclosure }}>
      {children}
    </EmailTemplatesSidebarContext.Provider>
  );
};

// Email Templates Provider
export const EmailTemplatesProvider: FC<EmailTemplatesProviderPropsI> = ({
  children,
}) => {
  const [activeTab, setActiveTab] = useState<
    ValueOfObjectFields<typeof EMAIL_TEMPLATES_TABS>
  >(EMAIL_TEMPLATES_TABS.COMPOSE);

  /**
   * we might want  to have initial draft
   * when we open the sidebar
   * for example when we open the sidebar from the post call email section
   * we want to have the initial draft for the followup email
   */
  const [initialDraft, setInitialDraft] = useState<EmailDraftI | undefined>();

  const emailTemplatesManager = useEmailTemplatesManager();
  const emailTemplateDrafts = useEmailTemplateDrafts();
  const emailTemplateSequencing = useEmailTemplateSequencingContext();

  const { activeTemplate, activeTemplateId } = emailTemplatesManager;
  const { activeEmailSequenceItemId } = emailTemplateSequencing;
  const { drafts } = emailTemplateDrafts;

  /**
   * NOTE
   * If the email is part of a sequence, we need to pass the sequence order
   */
  const optionalParams = useSelectorEmailAIGenerationOptionalParams(
    drafts,
    activeTemplateId as string,
    activeEmailSequenceItemId as string
  );

  const emailData = useAccountEmailAIGeneration(activeTemplate, optionalParams);

  return (
    <EmailTemplatesContext.Provider
      value={{
        activeTab,
        setActiveTab,

        initialDraft,
        setInitialDraft,

        ...emailTemplatesManager,
        ...emailTemplateDrafts,
        ...emailData,
        ...emailTemplateSequencing,
      }}
    >
      {children}
    </EmailTemplatesContext.Provider>
  );
};
